/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Box, Grid, Paper, Typography } from '@material-ui/core';
import { FC, Fragment } from 'react';
import useContentMgmt from '../../../hooks/useContentMgmt';
import theme from '../../../themes/theme';
import colors from '../../../themes/main-colors';
import { useIsMobile } from '../../../util/style-utils';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import Pagination from '../../pagination';
import { InvoiceDesktopView } from './table/desktop';
import { PaginationProps } from '../../pagination/Pagination';
import { PageSize } from '../../pagination/types';
import ProfilePictureMenu from './ProfilePictureMenu';
import {
  TableControlsActions,
  useInvoiceList,
} from '../../../hooks/invoice-payment-portal/useInvoiceList';
import Menu from '../../menu';
import { MenuItem } from '../../menu/Menu';

import { InvoiceDetails } from '../../../__generated__/pge-types';

import ROUTES from '../../../routes';
import { Link } from 'gatsby';
import Backdrop from '../../backdrop';
import useDownloadBill from '../../../hooks/useDownloadBill';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import { MobileView } from './table/mobile';

export const DATE_FORMAT = 'MM/dd/yyyy';

export type InvoiceActionsType = (invoice: InvoiceDetails) => JSX.Element;
interface Props {
  path?: string;
}

const InvoiceList: FC<Props> = () => {
  const isMobile = useIsMobile();

  const { content, isContentLoading } = useContentMgmt(
    microcopyGroupIds.INVOICE_LISTING,
  );
  const {
    loading,
    invoices,
    pagination,
    tableControls,
    searchParams,
  } = useInvoiceList();

  const downloader = useDownloadBill();
  const { wrapWithLoader } = useWrapWithLoader();
  const downloadBill = wrapWithLoader(
    async (encryptedBillId: string) => {
      if (encryptedBillId) {
        const billId = encryptedBillId;

        const linkSource = await downloader.downloadBill(billId, false, false);
        const downloadLink = document.createElement('a');

        downloadLink.href = linkSource;
        downloadLink.download = `${content
          .get('VIEW_BILL')!
          .replace(' ', '_')}-${Date.now()}.pdf`;
        downloadLink.click();
      }
    },
    () => content.get('NO_BILL_FOUND')!,
  );

  const invoiceActions = (invoice: InvoiceDetails): JSX.Element => {
    const { billId, amountDue } = invoice;
    const generateActionLinks = (
      action: string | undefined,
      link: string | undefined,
      menuItems?: Array<MenuItem>,
    ) =>
      link ? (
        <Link
          to={link}
          css={{
            textDecoration: 'none',
            color: colors.noirBlur,
          }}
        >
          <Box
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box width="88%">
              <Typography
                css={{
                  fontSize: theme.spacing(2),
                  fontWeight: 'bold',
                  color: colors.sparkBlue,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '18px',
                }}
              >
                {action}
              </Typography>
            </Box>
          </Box>
        </Link>
      ) : (
        <Box
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            '&>button': {
              padding: 0,
            },
          }}
        >
          <Typography
            css={{
              fontSize: theme.spacing(2),
            }}
          >
            {action}
          </Typography>
          <Menu items={menuItems!} />
        </Box>
      );

    if (amountDue && amountDue >= 0) {
      // Awaiting payment
      return generateActionLinks(
        content.get('PAY_BILL'),
        ROUTES.INVOICE_PAYMENT_PORTAL_INVOICE_PAYMENT_INFO.replace(
          ':invoiceId',
          billId!,
        ),
      );
    } else {
      return <Typography>{'-'}</Typography>;
    }
    return <Box></Box>;
  };
  const paginationProps: PaginationProps = {
    count: (pagination?.totalPages || 1) * 10,
    pageSize: 10,
    pageSizes: [],
    page: pagination?.page || 1,
    visibleOnSinglePage: true,
    onPageChange: (newPage: number, newPageSize: PageSize) => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      tableControls(TableControlsActions.PAGINATION, newPage);
    },
  };

  return (
    <Fragment>
      {loading && <Backdrop forceOpen />}

      <Grid
        container
        css={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.spacing(7),
          marginBottom: theme.spacing(7),
        }}
      >
        <Grid item xs={10}>
          <Typography
            variant="h1"
            css={{
              marginBottom: theme.spacing(2),
              ...(isMobile && { fontSize: theme.spacing(3) }),
            }}
          >
            {content.get('INVOICE_PAYMENT_TITLE')}
          </Typography>
          <Paper
            css={{
              padding: theme.spacing(2),
              borderRadius: 0,
              boxShadow: 'none',
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                css={{
                  //ignore next code line for better readability
                  //prettier-ignore
                  ...(!isMobile && { margin: `${theme.spacing(2)}px 0 ${theme.spacing(4,)}px ${theme.spacing(2)}px` }),
                  ...(isMobile
                    ? {
                        fontSize: theme.spacing(2.3),
                        marginBottom: theme.spacing(2),
                      }
                    : { fontSize: theme.spacing(3) }),
                  color: colors.noirBlur,
                  fontFamily: 'Forma-DJR-Display',
                  letterSpacing: '1px',
                }}
              >
                {content.get('INVOICE_LIST')}
              </Typography>
              <Box>
                <ProfilePictureMenu content={content} />
              </Box>
            </Box>
            {isMobile ? (
              <MobileView
                content={content}
                invoices={invoices as InvoiceDetails[]}
                invoiceActions={invoiceActions}
                tableControls={tableControls}
                searchParams={searchParams}
                downloadBill={downloadBill}
              />
            ) : (
              <InvoiceDesktopView
                content={content}
                invoices={invoices as InvoiceDetails[]}
                invoiceActions={invoiceActions}
                tableControls={tableControls}
                searchParams={searchParams}
                downloadBill={downloadBill}
              />
            )}
            <Grid>
              <Pagination {...paginationProps} />
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default InvoiceList;
